import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { DEFAULT_LATITUDE, DEFAULT_LONGITUDE, errorMsg, tryGetAddress } from "../../../../../Apis/functions";
import { GetAddressFromLatLng } from "../../../../../reduxToolkit/Address/AddressApi";
import { GetAllLocationsByUserAndByDates } from "../../../../../reduxToolkit/TrafficFines/TrafficFinesApi";
import { useParams } from "react-router-dom";
import { FINES_LOCATION_MODEL } from "../Model/Model";
import HandleChange from "../../../../../Helpers/HandleChange";
import { debounce } from "lodash";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTitle: Full Stack Developer
 *
 * TrafficDetailsMapApp
 *
 * Hook-like component to retrieve and manage geolocation and traffic fine details based on user and date range.
 * Handles API calls, UI data binding, address resolution, and debounced map movement.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.dateRange - Current date range, with a `.toRegister()` method returning `{start, end}` timestamps.
 * @param {Function} props.setFinesCount - Setter function to update the number of fines fetched.
 *
 * @returns {Object} Object containing handlers and state used in TrafficDetailsMap
 * @property {Array} locations - Array of fine locations
 * @property {string} searchLocation - Value of search/filter for locations
 * @property {Function} dispatch - Redux dispatch function
 * @property {Function} t - i18next translation function
 * @property {string} direction - Address of current map location
 * @property {Function} handleChange - Handler for updating input-bound state
 * @property {Object} item - Current traffic fine location model
 * @property {Function} handleMapMove - Debounced function to handle map movement and fetch address
 */
export const TrafficDetailsMapApp = ({ dateRange, setFinesCount, modified }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { id } = useParams();

    /** Current traffic fine location item */
    const [item, setItem] = useState({ ...FINES_LOCATION_MODEL });

    /** Resolved address for current location */
    const [direction, setDirection] = useState();

    /** List of fine locations for user and date range */
    const [locations, setLocations] = useState([]);

    /** Filter input for locations */
    const [searchLocation, setSearchLocation] = useState("");

    const { handleChange } = HandleChange({ model: item, setModel: setItem });

    /** Refs */
    const timeoutRef = useRef(null);
    const elementRef = useRef(null);

    /** Initialize item coordinates */
    useEffect(() => {
        setItem((prevState) => ({
            ...prevState,
            latitude: DEFAULT_LATITUDE,
            longitude: DEFAULT_LONGITUDE,
        }));
    }, []);

    /**
     * Fetch address from latitude and longitude
     * @param {number} lat - Latitude
     * @param {number} lng - Longitude
     */
    const getLocationData = (lat, lng) => {
        dispatch(GetAddressFromLatLng({ lat, lng, location: 7 })).then(({ payload }) => {
            if (payload?.success) {
                setDirection(payload?.location);
            } else setDirection("");
        });
    };

    /** Debounced function to handle map move and fetch address */
    const handleMapMove = debounce((lat, lng) => {
        tryGetAddress(lat, lng, dispatch, setDirection);
    }, 800);

    /** Update direction whenever item coordinates change */
    useEffect(() => {
        getLocationData(item?.latitude, item?.longitude);
    }, [item]);

    /** Update CSS variable for top position when date range changes */
    useLayoutEffect(() => {
        if (elementRef?.current) {
            const rect = elementRef.current.getBoundingClientRect();
            const distanceTop = rect.top + 0;
            elementRef.current.style.setProperty("--top-value", `${distanceTop}px`);
        }
    }, [dateRange]);

    /** Reset search location on mount */
    useEffect(() => setSearchLocation(""), []);

    /** Clear any timeout on unmount */
    useEffect(() => () => clearTimeout(timeoutRef.current), []);

    /**
     * Fetch all fine locations for the user within the current date range
     * @async
     */
    const getData = async () => {
        const { start, end } = dateRange.toRegister();
        setLocations([]);

        const bodyLocations = {
            userId: id,
            startDate: start,
            endDate: end,
        };

        try {
            const [locationsRes] = await Promise.all([
                dispatch(GetAllLocationsByUserAndByDates({ body: bodyLocations })).unwrap(),
            ]);

            if (locationsRes.data.success) {
                setLocations(locationsRes.data.data);
                setFinesCount(locationsRes?.data?.data?.length);
            } else {
                errorMsg(locationsRes.data.code);
            }
        } catch (err) {}
    };

    /** Refresh data whenever date range changes */
    useEffect(() => {
        getData();
    }, [dateRange, modified]);

    return {
        locations,
        searchLocation,
        dispatch,
        t,
        direction,
        handleChange,
        item,
        handleMapMove,
    };
};
